import React from 'react';
import { Route } from 'react-router-dom';
import { lazyWithRetry } from '../utils/lazyWithRetry';
import ProtectedRoute from '../features/auth/ProtectedRoute';
import DashboardSkeleton from '../components/ui/layout/DashboardSkeleton';

const UserManagementPanel = lazyWithRetry(() => import('../features/admin/users/UserManagementPanel'));
const AdminBillingDashboard = lazyWithRetry(() => import('../features/admin/billing/AdminBillingDashboard'));
const AdminSupportPanel = lazyWithRetry(() => import('../features/admin/AdminSupportPanel'));
const AdminResourcesPanel = lazyWithRetry(() => import('../features/admin/AdminResourcesPanel'));
const AuditPanel = lazyWithRetry(() => import('../features/admin/AuditPanel'));
const TariffEditor = lazyWithRetry(() => import('../features/admin/finance/TariffEditor'));
const AnnouncementSystem = lazyWithRetry(() => import('../features/admin/AnnouncementSystem'));
const WeeklyScheduler = lazyWithRetry(() => import('../features/operations/WeeklyScheduler'));
const KanbanBoard = lazyWithRetry(() => import('../features/admin/kanban/KanbanBoard'));
const AdminFranchiseView = lazyWithRetry(() => import('../features/admin/AdminFranchiseView'));
const AcademyAdmin = lazyWithRetry(() => import('../features/academy/admin/AcademyAdmin'));
const AdminFlyderDashboard = lazyWithRetry(() => import('../features/admin/flyder/AdminFlyderDashboard'));

interface AdminRoutesProps {
    targetFranchiseId: string | null;
}

export function AdminRoutes({ targetFranchiseId }: AdminRoutesProps) {
    return (
        <>
            <Route path="admin/users" element={
                <ProtectedRoute requireAdmin={true}>
                    <UserManagementPanel />
                </ProtectedRoute>
            } />
            <Route path="admin/support" element={
                <ProtectedRoute requireAdmin={true}>
                    <AdminSupportPanel />
                </ProtectedRoute>
            } />
            <Route path="admin/resources" element={
                <ProtectedRoute requireAdmin={true}>
                    <AdminResourcesPanel />
                </ProtectedRoute>
            } />
            <Route path="admin/audit" element={
                <ProtectedRoute requireAdmin={true}>
                    <AuditPanel />
                </ProtectedRoute>
            } />
            <Route path="admin/billing" element={
                <ProtectedRoute requireAdmin={true}>
                    <AdminBillingDashboard />
                </ProtectedRoute>
            } />
            <Route path="admin/tariffs" element={
                <ProtectedRoute requireAdmin={true}>
                    <TariffEditor onClose={() => { }} />
                </ProtectedRoute>
            } />
            <Route path="admin/communications" element={
                <ProtectedRoute requireAdmin={true}>
                    <div className="space-y-6 p-0 sm:p-4 md:p-8"><AnnouncementSystem /></div>
                </ProtectedRoute>
            } />
            <Route path="admin/shifts" element={
                <ProtectedRoute requireAdmin={true}>
                    <div className="min-h-0 space-y-6 p-0 sm:p-4 md:p-8 lg:h-[calc(100vh-64px)] lg:overflow-hidden">
                        <WeeklyScheduler franchiseId={targetFranchiseId || ''} readOnly={false} />
                    </div>
                </ProtectedRoute>
            } />
            <Route path="admin/kanban" element={
                <ProtectedRoute requireAdmin={true}>
                    <div className="min-h-0 space-y-6 p-0 sm:p-4 md:p-8 lg:h-[calc(100vh-64px)] lg:overflow-hidden">
                        <React.Suspense fallback={<DashboardSkeleton />}>
                            <KanbanBoard />
                        </React.Suspense>
                    </div>
                </ProtectedRoute>
            } />
            <Route path="admin/franchise/:franchiseId" element={
                <ProtectedRoute requireAdmin={true}>
                    <AdminFranchiseView />
                </ProtectedRoute>
            } />
            <Route path="admin/academy" element={
                <ProtectedRoute requireAdmin={true}>
                    <AcademyAdmin />
                </ProtectedRoute>
            } />
            <Route path="admin/flyder" element={
                <ProtectedRoute requireAdmin={true}>
                    <AdminFlyderDashboard franchiseId={targetFranchiseId || undefined} />
                </ProtectedRoute>
            } />
        </>
    );
}
